<template>
	<div class="machine-content">
		<section class="mp-section mp-section--torque" v-if="isShowMachineProperties">
			<div class="mp-section__head">
				<div>
					<div class="mp-section__en">TORQUE &amp; As</div>
					<div class="mp-section__zh">拧紧与截面积</div>
				</div>
			</div>
			<div class="mp-torque">
				<div class="mp-calc">
					<div class="mp-calc__hint">拧紧力矩计算器 · T = K · P · D</div>
					<div class="mp-calc__field">
						<label>P — 预紧力系数</label>
						<div class="mp-calc__row">
							<el-select class="mp-calc__select mp-calc__select--sm" v-model="installAppObj.preload" placeholder="">
								<el-option v-for="(item, index) in installApplication.preloadList" :key="'p'+index" :label="item" :value="item" />
							</el-select>
							<span class="mp-calc__aside">VDI 推荐 0.6–0.7</span>
						</div>
					</div>
					<div class="mp-calc__field">
						<label>K — 扭矩系数（区间）</label>
						<div class="mp-calc__row">
							<el-select class="mp-calc__select mp-calc__select--sm" v-model="installAppObj.minTorque" placeholder="">
								<el-option v-for="(item, index) in installApplication.minTorqueList" :key="'kmin'+index" :label="item" :value="item" />
							</el-select>
							<span class="mp-calc__dash">—</span>
							<el-select class="mp-calc__select mp-calc__select--sm" v-model="installAppObj.maxTorque" placeholder="">
								<el-option v-for="(item, index) in installApplication.maxTorqueList" :key="'kmax'+index" :label="item" :value="item" />
							</el-select>
						</div>
					</div>
					<div class="mp-calc__result">
						<label>T — 拧紧力矩</label>
						<div class="mp-calc__torque">
							<span class="mp-calc__value">{{ calculateResultValue }}</span>
							<span class="mp-calc__unit">N·m</span>
						</div>
						<p v-if="yieldPoint == 0" class="mp-calc__warn">（当产品无等级要求时，不计算扭矩）</p>
					</div>
				</div>
				<div class="mp-torque__side">
					<div class="mp-formula">
						<div class="mp-formula__title">
							<span class="mp-formula__badge">公式</span>
							<code>T = K × P × D</code>
						</div>
						<p class="mp-formula__desc">T—力矩(N·m)　P—预紧力(kN)　D—公称直径(mm)</p>
						<ul class="mp-formula__list">
							<li>有润滑的表面氧化面，扭矩系数可参考 K=0.20</li>
							<li>预紧力一般取：P=(0.6–0.7)×屈服点(N/mm²)×应力截面积(mm²)</li>
							<li>屈服点与应力截面积参照 GB/T 3098.1；0.6–0.7 依 VDI 2230 推荐</li>
						</ul>
					</div>
					<div class="mp-as" v-if="Object.keys(threadStressArea).length">
						<div class="mp-as__sketch" aria-hidden="true">
							<svg width="72" height="72" viewBox="0 0 72 72" fill="none">
								<circle cx="36" cy="36" r="28" stroke="#2F5D8A" stroke-width="1.5" fill="#E8F0F7"/>
								<circle cx="36" cy="36" r="18" stroke="#2F5D8A" stroke-width="1.2" stroke-dasharray="3 2" fill="#fff"/>
								<circle cx="36" cy="36" r="10" stroke="#E8A317" stroke-width="1.5" fill="#FFF8E8"/>
								<text x="36" y="39" text-anchor="middle" font-size="9" fill="#2F5D8A">As</text>
							</svg>
						</div>
						<div class="mp-as__chart-wrap">
							<div class="mp-as__label">螺纹应力截面积 As</div>
							<div class="mp-as__body">
								<div ref="asEchartsDom" class="mp-as__chart"></div>
								<div class="mp-as__meta">
									<div v-if="nominalValue"><span>公称直径 D</span><strong>{{ nominalValue }}</strong></div>
									<div class="mp-as__note">参与预紧力与扭矩计算</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="mp-section" v-if="mechanicalPropertiesList.length">
			<div class="mp-section__head">
				<div>
					<div class="mp-section__en">MECHANICAL PROPERTIES</div>
					<div class="mp-section__zh">机械性能</div>
				</div>
				<span v-if="isShowMachineProperties" class="mp-section__tag">GB/T 3098.1</span>
			</div>
			<div class="mp-mech">
				<div class="mp-mech__sketch" aria-hidden="true">
					<svg width="64" height="72" viewBox="0 0 64 72" fill="none">
						<rect x="26" y="4" width="12" height="10" rx="1" stroke="#2F5D8A" stroke-width="1.2" fill="#E8F0F7"/>
						<path d="M22 14 L42 14 L38 28 L26 28 Z" stroke="#2F5D8A" stroke-width="1.2" fill="#fff"/>
						<rect x="26" y="28" width="12" height="22" stroke="#E8A317" stroke-width="1.5" fill="#FFF8E8"/>
						<path d="M26 50 L38 50 L42 64 L22 64 Z" stroke="#2F5D8A" stroke-width="1.2" fill="#fff"/>
						<rect x="22" y="64" width="20" height="6" rx="1" stroke="#2F5D8A" stroke-width="1.2" fill="#E8F0F7"/>
					</svg>
				</div>
				<ul class="mp-mech__list">
					<li
						v-for="(item, index) in mechanicalPropertiesList"
						:key="index"
						class="mp-mech__row"
						:class="{ 'is-accent': isYieldProperty(item.propertyName) }"
					>
						<span class="mp-mech__name">{{ item.propertyName }}</span>
						<span class="mp-mech__val">{{ item.propertyValue }}</span>
					</li>
				</ul>
			</div>
		</section>

		<section class="mp-section" v-if="chemicalComponentList.length">
			<div class="mp-section__head">
				<div>
					<div class="mp-section__en">CHEMICAL COMPOSITION</div>
					<div class="mp-section__zh">化学成分</div>
				</div>
				<span class="mp-section__extra">质量分数 / 范围值</span>
			</div>
			<div class="mp-chem">
				<div ref="echartsDom" class="mp-chem__chart"></div>
			</div>
		</section>

		<section class="mp-section mp-section--friction" v-if="isShowMachineProperties && applicationDescHtml">
			<div class="mp-section__head">
				<div>
					<div class="mp-section__en">FRICTION CLASS</div>
					<div class="mp-section__zh">摩擦系数等级</div>
				</div>
			</div>
			<div class="mp-friction-tip">
				<span class="mp-friction-tip__icon">!</span>
				<p>应努力达到摩擦系数等级中列出的摩擦系数，从而获得尽可能高的预紧力，同时数值分散也较小，这也就是不自动的使用最小的值，同时目前的摩擦系数数值分散与等级的数值分散相符，该表格在室温时有效</p>
			</div>
			<div class="mp-friction-html" v-html="applicationDescHtml"></div>
		</section>

		<div
			v-if="!isShowMachineProperties && !chemicalComponentList.length && !mechanicalPropertiesList.length"
			class="mp-empty"
		>
			暂无数据
		</div>
	</div>
</template>

<script>
export default {
	name: 'MachinePanels',
	props: {
		installApplication: { type: Object, required: true },
		installAppObj: { type: Object, required: true },
		calculateResultValue: { type: String, required: true },
		yieldPoint: { required: true },
		threadStressArea: { type: Object, required: true },
		nominalValue: { required: true },
		chemicalComponentList: { type: Array, required: true },
		mechanicalPropertiesList: { type: Array, required: true },
		applicationDescHtml: { type: String, default: '' },
		isShowMachineProperties: { type: Boolean, required: true }
	},
	methods: {
		isYieldProperty(name) {
			return /屈服/.test(name || '')
		}
	}
}
</script>
